import { CancelSubscriptionConfirmation } from "components/CancelSubscriptionConfirmation";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { RocketPurpleAnimate } from "components/icons/RocketPurpleAnimate";
import { SparkleGroup } from "components/icons/SparkleGroup";
import {
  BenefitPoint,
  SubscriptionInfo,
  VerticalRow,
} from "components/SubscriptionOverviewComponents";
import {
  BOOKKEEPING_MONTHLY_PLUS,
  BOOKKEEPING_V2,
  BOOKKEEPING_V2_MANAGE,
} from "constants/addons";
import * as BILLING_CYCLES from "constants/billingCycles";
import { MMM_YYYY } from "constants/date";
import * as STRIPE_COUPON_TYPES from "constants/stripeCouponTypes";
import dayjs from "dayjs";
import { ACCOUNTING_METHODS_MAP, BILLING_CYCLE } from "dictionaries";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { BooksSubscriptionModal } from "pages/Books/BooksDashboard/BooksSubscriptionModal.tsx";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import SmallSparkle from "static/images/SmallSparkle.svg";
import { useUpdateSubscriptionMutation } from "store/apis/subscriptions";
import {
  AddonData,
  resetAllAddonDataPayload,
  setAddonData,
  setAddonType,
} from "store/slices/addons";
import { RootState } from "store/store";
import { BackendError } from "types/utils/error";
import { SubscribeStep } from "./BookkeepingService";

type InitialValuesType = {
  selected_tier: string;
  schedule_date: string;
  accounting_method: string;
  amount: number;
};
const BILLING_MAP = {
  [BILLING_CYCLES.MONTHLY]: "Monthly",
  [BILLING_CYCLES.QUARTERLY]: "Quarterly",
  [BILLING_CYCLES.YEARLY]: "Yearly",
};

export const BookkeepingServiceManage = () => {
  const dispatch = useDispatch();
  const entityId = useCurrentEntityId();
  const { addonType, addonData } = useSelector(
    (state: RootState) => state.addonsSlice
  );
  const { isCustomer } = useRoleBasedView();
  const [showAddonModal, setShowAddonModal] = useState<boolean>(
    addonType === BOOKKEEPING_V2_MANAGE
  );
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [updateSubscription, { isLoading: isUpdating }] =
    useUpdateSubscriptionMutation();
  const { alertToast } = useToast();
  const history = useHistory();

  const {
    subscription_name,
    tier_information,
    amount: addonAmount,
    stripe_coupon,
    scheduled_date,
    group_subscription,
    billing_cycle,
    uuid,
    metadata,
  } = addonData || {};

  const groupSubscription = group_subscription?.[0];
  const groupSubscriptionId = groupSubscription?.group_subscription_id;
  const accounting_method =
    groupSubscription?.accounting_method as keyof typeof ACCOUNTING_METHODS_MAP;
  const tierInformation = groupSubscription?.tier_information;

  const close = () => {
    setShowAddonModal(false);
    dispatch(setAddonType(""));
    dispatch(resetAllAddonDataPayload());
  };

  const onSubmit = async (values: InitialValuesType) => {
    const tier = tier_information?.find(
      ({ uuid }) => uuid === values.selected_tier
    );
    if (uuid && tier) {
      try {
        await updateSubscription({
          entityId: entityId,
          subscriptionId: groupSubscriptionId,
          payload: {
            scheduled_date: values.schedule_date,
            accounting_method: values.accounting_method,
            custom_price: Number(values.amount),
            tier_id: tier.uuid,
          },
        }).unwrap();
        close();
      } catch (error) {
        alertToast({ message: (error as BackendError).data?.error?.message });
      }
    }
  };

  const handleContactUs = () => {
    close();
    history.push("/chat");
  };

  const openCancelSubscription = () => {
    setShowCancelModal(true);
    setShowAddonModal(false);
  };

  if (!groupSubscriptionId || !uuid) {
    return (
      <Modal.Root onOpenChange={close}>
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Manage Plan</Modal.Title>
            <Modal.Close />
          </Modal.Header>

          <Modal.Body>Cannot find subscription.</Modal.Body>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button>Close</Button>
            </Modal.RawClose>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
    );
  }

  if (isCustomer) {
    return (
      <>
        <Modal.Root open={showAddonModal} onOpenChange={close}>
          <Modal.Content>
            <Modal.Header className="t-bg-purple-0 t-flex t-justify-between t-rounded-tl-lg t-rounded-tr-lg !t-pl-6">
              <div className="t-flex t-items-center t-gap-4">
                <RocketPurpleAnimate />
                <div className="t-space-y-1.5">
                  <div className="t-text-subtext t-text-text-100">
                    {subscription_name}
                  </div>
                  <div className="t-flex t-gap-1.5 t-items-center">
                    <span className="t-text-subtext t-text-neutral-80">
                      <AmountSuperScript
                        amount={Number(
                          stripe_coupon?.discounted_amount || addonAmount
                        )}
                      />
                      {billing_cycle && (
                        <span>/{BILLING_CYCLE[billing_cycle]}</span>
                      )}
                    </span>
                    {stripe_coupon && (
                      <span className="t-text-body-sm t-flex t-gap-0.5 t-text-dark_green-70 t-px-1.5 t-py-0.5 t-bg-dark_green-10 t-rounded-2xl">
                        {stripe_coupon.discount_type ===
                        STRIPE_COUPON_TYPES.PERCENT
                          ? `${stripe_coupon.discount_rate}%`
                          : `$${stripe_coupon.discount_rate}`}{" "}
                        Off with {stripe_coupon?.coupon_code}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <Modal.Close />
            </Modal.Header>
            <Modal.Body>
              <div className="t-grid t-grid-cols-5 t-items-center t-gap-y-6 t-mb-6 t-overflow-hidden">
                <SubscriptionInfo
                  label="Started from"
                  value={
                    groupSubscription.started_on
                      ? dayjs(groupSubscription.started_on).format(MMM_YYYY)
                      : "-"
                  }
                />
                <VerticalRow />
                <SubscriptionInfo
                  label="Frequency"
                  value={billing_cycle ? BILLING_MAP[billing_cycle] : "-"}
                />
                <VerticalRow />
                <SubscriptionInfo
                  label="Next Billing Date"
                  value={
                    scheduled_date
                      ? dayjs(scheduled_date).format(MMM_YYYY)
                      : "-"
                  }
                />
                <SubscriptionInfo
                  label="Accounting Method"
                  value={ACCOUNTING_METHODS_MAP[accounting_method] ?? "-"}
                />
                <VerticalRow />
                <SubscriptionInfo
                  label="Estimated expense vol."
                  value={
                    <>
                      <AmountSuperScript
                        amount={Number(tierInformation?.lower_range)}
                      />{" "}
                      to{" "}
                      <AmountSuperScript
                        amount={Number(tierInformation?.higher_range)}
                      />
                    </>
                  }
                />
                <VerticalRow />
                <SubscriptionInfo
                  label="Tier Pricing"
                  value={<AmountSuperScript amount={Number(addonAmount)} />}
                />
              </div>
              <div className="t-border t-border-solid t-border-purple-0 t-p-1 t-rounded-lg">
                <div className="t-flex t-items-center t-gap-2  t-px-3 t-py-1">
                  <img src={SmallSparkle} alt="SmallSparkle" />
                  <span className="t-text-body-sm t-text-text-60">
                    Plan benefits
                  </span>
                </div>
                <div className="t-rounded-lg t-bg-surface-lighter-grey t-space-y-1.5 t-p-3">
                  {metadata?.benefits?.map((feature, index) => (
                    <BenefitPoint key={index} description={feature} />
                  ))}
                </div>
              </div>
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <Button customType="ghost" onClick={handleContactUs}>
                Contact us
              </Button>
              <Button onClick={openCancelSubscription}>
                Cancel subscription
              </Button>
            </Modal.FooterButtonGroup>
          </Modal.Content>
        </Modal.Root>
        <BookKeepingProCancel
          showCancelModal={showCancelModal}
          setShowCancelModal={setShowCancelModal}
        />
      </>
    );
  }

  return (
    <>
      <Modal.Root open={showAddonModal} onOpenChange={close}>
        <Modal.Content>
          <FormikForm<InitialValuesType>
            initialValues={{
              selected_tier: tierInformation?.uuid || "",
              schedule_date: scheduled_date || "",
              accounting_method: accounting_method,
              amount: addonAmount || 0,
            }}
            // @ts-ignore
            onSubmit={onSubmit}
          >
            <>
              <Modal.Header>
                <Modal.Title>Manage Plan</Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body>
                <SubscribeStep subscriptionId={uuid} />
              </Modal.Body>
              <Modal.Footer className="t-flex t-justify-between">
                <Button customType="ghost" onClick={openCancelSubscription}>
                  Cancel subscription
                </Button>
                <Button
                  customType="primary"
                  isLoading={isUpdating}
                  disabled={isUpdating}
                >
                  Update
                </Button>
              </Modal.Footer>
            </>
          </FormikForm>
        </Modal.Content>
      </Modal.Root>
      <BookKeepingProCancel
        showCancelModal={showCancelModal}
        setShowCancelModal={setShowCancelModal}
      />
    </>
  );
};

export const BookKeepingProCancel = ({
  showCancelModal,
  setShowCancelModal,
}: {
  showCancelModal: boolean;
  setShowCancelModal: (newState: React.SetStateAction<boolean>) => void;
}) => {
  const dispatch = useDispatch();
  const { addonData } = useSelector((state: RootState) => state.addonsSlice);
  const cancelConfirmationModal = useModal();
  const exploreBooksSubscription = useModal();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const history = useHistory();
  const [subscriptionModalTab, setSubscriptionModalTab] =
    useState<string>("Services");

  const handleCloseSubscription = () => {
    setShowCancelModal(false);
    cancelConfirmationModal.close();
    dispatch(setAddonType(""));
    dispatch(setAddonData(null));
  };

  const onManageHandler = (addon: AddonData) => {
    history.push("/settings/services");
    if (addon?.subscription_type === BOOKKEEPING_V2) {
      dispatch(setAddonData(addon));
      dispatch(setAddonType(BOOKKEEPING_V2_MANAGE));
    }
  };

  const openExploreSubscription = () => {
    exploreBooksSubscription.open();
    setShowCancelModal(false);
  };

  const closeExploreSubscription = () => {
    exploreBooksSubscription.close();
    handleCloseSubscription();
  };

  const openCancelSubscriptionConfirmation = () => {
    cancelConfirmationModal.open();
    setShowCancelModal(false);
  };

  return (
    <>
      <Modal.Root open={showCancelModal} onOpenChange={setShowCancelModal}>
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Cancel Subscription</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <div className="t-text-body-sm t-text-text-60">
              Are you sure you want to cancel subscription?
            </div>
            <div className="t-text-body-sm t-text-text-30 t-mt-1 t-mb-4">
              Inkle will no longer manage your ongoing bookkeeping, and You’ll
              no longer have a dedicated monthly Inkle Bookkeeper.
            </div>
            <div className="t-p-4 t-rounded-lg t-border t-border-solid t-border-blue-10 t-bg-blue-0 t-flex t-gap-2">
              <div className="t-text-blue-70">
                <SparkleGroup />
              </div>
              <div>
                <div className="t-text-body-sm t-text-text-30">
                  You can still purchase our one-time bookkeeping catch-up to
                  manage your seasonal filings.
                </div>
                <button
                  className="all:unset t-cursor-pointer t-text-purple t-text-subtext-sm"
                  onClick={openExploreSubscription}
                >
                  View details
                </button>
              </div>
            </div>
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Button onClick={handleCloseSubscription}>
              Stay on current plan
            </Button>
            <Button
              customType="primary"
              onClick={openCancelSubscriptionConfirmation}
            >
              Continue
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>{" "}
      {cancelConfirmationModal.isOpen && (
        <CancelSubscriptionConfirmation
          subscriptionId={
            addonData?.group_subscription?.[0]?.group_subscription_id
          }
          close={handleCloseSubscription}
          isOpen={cancelConfirmationModal.isOpen}
          handleCancelSubscription={handleCloseSubscription}
        />
      )}
      <BooksSubscriptionModal
        show={exploreBooksSubscription.isOpen}
        onClose={closeExploreSubscription}
        entityId={entityId}
        groupId={groupId}
        onManage={onManageHandler}
        subscriptionModalTab={subscriptionModalTab}
        setSubscriptionModalTab={setSubscriptionModalTab}
      />
    </>
  );
};
